/**
 * Which camera, and how a publisher changes their mind.
 *
 * Wave 5, defect D10. `publish.ts` asked for `facingMode: { ideal: 'environment' }`
 * unconditionally, which is a request for a REAR camera. On a phone in a gym that
 * is exactly right: the rear camera is the one pointed at the court. On a desktop
 * it is a hijack, and it was reported from a real machine on 2026-08-29:
 *
 *   FaceTime HD Camera (Built-in)   front-facing, reports "user"
 *   Lucas iPhone12 Pro Camera       Continuity Camera, rear, reports "environment"
 *
 * The browser did what it was told and published the operator's iPhone instead of
 * their webcam. Three things made that hard to notice and impossible to fix: the
 * constraint was unconditional, there was no picker, and the preview never said
 * which camera it had chosen. All three are addressed here; only the first was
 * the bug.
 *
 * Pure, because `src/client/` is the half of this repository with no runtime
 * harness (issue 010) and a decision in a click handler is a decision nothing
 * can test.
 */

/** The subset of `MediaDeviceInfo` that matters here, so tests need no DOM. */
export interface VideoInput {
  deviceId: string;
  label: string;
}

/**
 * Whether to prefer a rear camera when the operator has expressed no preference.
 *
 * A heuristic, and named as one. `maxTouchPoints` is the closest thing to "is
 * this a handheld" that does not involve parsing a user agent string, which is
 * a practice that has been wrong for twenty years. Its failure mode is a
 * touchscreen laptop preferring a rear camera it does not have, which resolves
 * to the browser's default anyway because `ideal` is a preference and not a
 * requirement, and which the picker then corrects in one tap.
 */
export function prefersRearCamera(maxTouchPoints: number): boolean {
  return maxTouchPoints > 0;
}

/**
 * The constraints for one camera.
 *
 * An explicit choice is `exact`, never `ideal`: a publisher who picked a camera
 * and silently got a different one is back to the defect this file exists to
 * close, one layer further in. If the chosen device has been unplugged,
 * `getUserMedia` rejects and the caller re-enumerates, which is a visible
 * failure rather than a silent substitution.
 */
export function cameraConstraints(deviceId: string | null, preferRear: boolean): MediaTrackConstraints {
  const shape = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    frameRate: { ideal: 30 },
  };
  if (deviceId !== null) return { ...shape, deviceId: { exact: deviceId } };
  return preferRear ? { ...shape, facingMode: { ideal: 'environment' } } : shape;
}

/**
 * Which camera to open, given what is plugged in and what was chosen last time.
 *
 * Returns null to mean "let the browser and the constraints decide", which is
 * the honest answer before anyone has chosen. A remembered device that is no
 * longer present returns null rather than an id that will fail: an operator who
 * unplugged a camera between games should get a working default, not an error.
 */
export function chooseCamera(devices: readonly VideoInput[], remembered: string | null): string | null {
  if (remembered === null) return null;
  return devices.some((d) => d.deviceId === remembered) ? remembered : null;
}

/**
 * What to call a camera on screen.
 *
 * `enumerateDevices` returns EMPTY labels until camera permission has been
 * granted, which is a privacy protection and not a bug, so this has to render
 * something useful for a device it cannot name. It never returns an empty
 * string, because a picker with blank rows is worse than one that admits it does
 * not know yet.
 */
export function cameraName(device: VideoInput, index: number): string {
  const label = device.label.trim();
  return label === '' ? `Camera ${index + 1}` : label;
}

/**
 * The line under the preview, which is the part that would have made D10 obvious
 * in one second instead of by recognising the picture.
 */
export function activeCameraLabel(
  devices: readonly VideoInput[],
  activeId: string | null,
  synthetic: boolean,
): string {
  if (synthetic) return 'Publishing a test pattern. No camera is in use.';
  if (activeId === null) return 'Using the default camera.';
  const i = devices.findIndex((d) => d.deviceId === activeId);
  const device = i === -1 ? undefined : devices[i];
  if (device === undefined) return 'Using a camera this page can no longer see.';
  return `Using ${cameraName(device, i)}.`;
}
